/**
 * "Suggest a feature" — the proactive door into the product listener. Same record, same
 * recorder, no need to wait for Copilot to fail first. The server writes the one-line summary
 * and picks the theme.
 */
import { useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Lightbulb, Loader2, Send } from "lucide-react";
import { SignalRecorder } from "./SignalRecorder";

export function SuggestFeatureDialog({ open, onOpenChange, tenantId }) {
  const [text, setText] = useState("");
  const [recording, setRecording] = useState(null);
  const [sending, setSending] = useState(false);

  const reset = () => { setText(""); setRecording(null); };
  const send = async () => {
    const body = text.trim() || (recording?.transcript ? recording.transcript.trim() : "");
    if (!body) { toast.error("Tell us what you'd like Ask Redline to do — a sentence or a recording."); return; }
    setSending(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/copilot/product-signals`, {
        verbatim: body, source: "suggest", recording_id: recording?.id || null,
      });
      toast.success(r.data.message);
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e, "That didn't send."));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!sending) { if (!o) reset(); onOpenChange(o); } }}>
      <DialogContent className="max-w-lg" data-testid="suggest-feature-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="font-heading text-xl font-extrabold flex items-center gap-2">
            <span className="h-8 w-8 rounded-xl bg-amber-100 flex items-center justify-center"><Lightbulb className="h-4 w-4 text-amber-600" strokeWidth={2} /></span>
            Suggest a feature
          </DialogTitle>
          <DialogDescription>
            Something you wish Ask Redline did, or a spot where it gets in your way. It goes straight to the team that builds it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} disabled={sending} maxLength={4000}
            placeholder="e.g. Let me generate and sell resale certificates from the dashboard…"
            data-testid="suggest-feature-text"
            className="w-full resize-none rounded-xl border border-sand-200 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-ink disabled:opacity-60" />
          <SignalRecorder tenantId={tenantId} value={recording} onChange={setRecording} disabled={sending} />
          <div className="flex items-center justify-end gap-2 pt-1">
            <button onClick={() => onOpenChange(false)} disabled={sending} data-testid="suggest-feature-cancel"
              className="rounded-full border border-sand-200 bg-white text-sm font-semibold px-4 py-2 text-ink/70 hover:bg-sand-50 disabled:opacity-50">Cancel</button>
            <button onClick={send} disabled={sending} data-testid="suggest-feature-send"
              className="rounded-full bg-ink text-white text-sm font-semibold px-5 py-2 hover:bg-ink-700 disabled:opacity-50 flex items-center gap-1.5">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" strokeWidth={2.5} />}
              {sending ? "Sending…" : "Send to the team"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
